/**
 * Custom hook for aggregation state management and data processing
 *
 * @module MetricDataDevTool/hooks/useAggregation
 */

import { useState, useMemo, useCallback } from 'react';
import { aggregateMetricData, getAggregationDescription } from '../services/aggregationService';

/**
 * Custom hook for managing aggregation state and processing data
 * @param {Array} rawData - Raw metric data array
 * @returns {Object} Hook state and methods
 */
export function useAggregation(rawData = []) {
  const [aggregationConfig, setAggregationConfig] = useState({
    byDate: false,
    byDayPart: false,
    byDiningOption: false
  });

  /**
   * Aggregate data based on current configuration
   */
  const aggregatedData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return [];
    }

    return aggregateMetricData(rawData, aggregationConfig);
  }, [rawData, aggregationConfig]);

  /**
   * Get human-readable description of current aggregation
   */
  const description = useMemo(() => {
    return getAggregationDescription(aggregationConfig);
  }, [aggregationConfig]);

  /**
   * Check if any aggregation is enabled
   */
  const isAggregating = useMemo(() => {
    return aggregationConfig.byDate ||
           aggregationConfig.byDayPart ||
           aggregationConfig.byDiningOption;
  }, [aggregationConfig]);

  /**
   * Update aggregation configuration
   */
  const updateConfig = useCallback((newConfig) => {
    setAggregationConfig(newConfig);
  }, []);

  /**
   * Toggle a specific aggregation dimension
   */
  const toggleDimension = useCallback((dimension) => {
    setAggregationConfig(prev => ({
      ...prev,
      [dimension]: !prev[dimension]
    }));
  }, []);

  /**
   * Reset aggregation to no aggregation
   */
  const reset = useCallback(() => {
    setAggregationConfig({
      byDate: false,
      byDayPart: false,
      byDiningOption: false
    });
  }, []);

  /**
   * Set all aggregations
   */
  const setAll = useCallback((enabled = true) => {
    setAggregationConfig({
      byDate: enabled,
      byDayPart: enabled,
      byDiningOption: enabled
    });
  }, []);

  return {
    aggregationConfig,
    aggregatedData,
    description,
    isAggregating,
    updateConfig,
    toggleDimension,
    reset,
    setAll
  };
}
